import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { article as articleData } from "shared/data";
import { notEmpty } from "shared/lib/utils";

import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import type { commentV2ContentItemContent$key } from "scmp-app/queries/__generated__/commentV2ContentItemContent.graphql";

import {
  AuthorName,
  Container,
  ContentColumn,
  HeadlineLine,
  Kicker,
  LettersIcon,
  StyledEntityLink,
  TextBlock,
  Thumbnail,
  Timestamp,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  reference: commentV2ContentItemContent$key;
};

const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  onClick,
  ref,
  reference,
}) => {
  const content = useFragment(
    graphql`
      fragment commentV2ContentItemContent on Content {
        entityId
        urlAlias
        ...entityLink
        ...hooksContentItemProvidersContent
          @arguments(
            withAuthorLink: true
            withHeadline: true
            withAuthorImage: true
            withPublishedDate: true
          )
        ... on Article {
          authors {
            entityId
          }
          seriesShortLabel
          seriesContainer {
            seriesShortLabel
          }
          types {
            value {
              entityId
            }
          }
        }
      }
    `,
    reference,
  );

  const providers = useContentItemProviders(content);

  const typeIds = new Set(
    (content.types ?? [])
      .flatMap(type => type?.value ?? [])
      .map(value => value?.entityId)
      .filter(notEmpty),
  );
  const isEditorial = typeIds.has(articleData.types.editorial.entityId);
  const isLetters = typeIds.has(articleData.types.letters.entityId);
  const seriesLabel = content.seriesContainer?.seriesShortLabel ?? content.seriesShortLabel;

  const kicker = (() => {
    if (isEditorial) return "Editorial";
    if (isLetters) return "Letters";
    return seriesLabel ?? "Opinion";
  })();

  const showAuthorName = !isEditorial && !isLetters;
  const showThumbnail = showAuthorName && (content.authors?.length ?? 0) === 1;

  return (
    <Container className={className} ref={ref}>
      <ContentColumn>
        <TextBlock>
          {showAuthorName && (
            <AuthorName onClick={() => onClick?.(content.entityId, content.urlAlias)}>
              {providers.author({ ignoreAuthorType: true, isMultiple: true })}
            </AuthorName>
          )}
          <StyledEntityLink
            onClick={() => onClick?.(content.entityId, content.urlAlias)}
            reference={content}
          >
            <HeadlineLine>
              <Kicker>{kicker}</Kicker>
              {" | "}
              {providers.headline({
                hideTag: true,
                hideTooltip: true,
                preferSocialHeadline: true,
              })}
            </HeadlineLine>
          </StyledEntityLink>
        </TextBlock>
        <Timestamp>{providers.publishedDate({ displayMode: "relative" })}</Timestamp>
      </ContentColumn>
      {showThumbnail && (
        <Thumbnail>
          {providers.authorImage({
            height: 120,
            ignoreAuthorType: true,
            isMultiple: false,
            width: 120,
            withBorder: false,
          })}
        </Thumbnail>
      )}
      {isLetters && <LettersIcon />}
    </Container>
  );
};

Component.displayName = "ContentItemCommentV2";

export const ContentItemCommentV2 = withHighlightedHeadline(Component);
